import { useContext, useEffect, useRef, type ReactNode } from 'react';

import { Botao, BotaoFechar, type NivelBotao } from './Botao';
import { ContextoDoPainel } from './contextoDoPainel';
import { focarAoAbrir } from '@/lib/foco';

/**
 * Uma ação pontual em janela, sem sair da tela (pedido do dono, 25/09):
 * registrar uma medição, cadastrar um tanque, autorizar uma ordem.
 *
 * Antes cada uma era um formulário fixo no meio da página, e três deles na
 * mesma tela eram três botões laranja competindo — a regra é um só. Na janela,
 * o laranja do formulário é o único que se alcança enquanto ela está aberta.
 *
 * `<dialog>` nativo pelo mesmo motivo do `BotaoDestrutivo`: `showModal()`
 * prende o foco dentro da caixa, devolve o foco ao botão que abriu e responde
 * ao `Esc` sem código nosso — e `showModal()` já a põe no CENTRO da tela,
 * que é o lugar do modal na R01 (p.29). O "✕" é o caractere, como no painel
 * de detalhe: o vocabulário de ícones do manual não tem entrada para "fechar".
 *
 * R01 (p.29), o modal: centro, 480–640 px, fundo preto obra a 40% bloqueando
 * a página atrás, cabeçalho fixo e só o corpo rola, altura máxima 85% da
 * tela. Abaixo de 768 px, tela cheia. A janela nunca é posicionada à mão.
 *
 * O conteúdo só é montado com a janela aberta: fechar e abrir de novo começa
 * um formulário limpo, e não o que ficou pela metade da última vez.
 *
 * O FOCO VAI PARA O PRIMEIRO CAMPO (R01, p.29). O `showModal()` sozinho foca
 * o primeiro focável da caixa, que era o ✕ do cabeçalho: quem abria pelo
 * teclado caía no botão de fechar. Sem campo, vai para o primeiro botão.
 *
 * O RODAPÉ (`acoes`) fica fora do corpo que rola, como no "Emitir folha,
 * corrigido" da p.29: ação principal à direita, a de voltar à esquerda dela.
 * Com conteúdo longo, e em tela cheia abaixo de 768 px, as ações continuam
 * presas embaixo.
 *
 * Aberta de dentro do painel de detalhe expandido, ela o reduz antes: nunca
 * duas modais empilhadas (p.29). Ao fechar, ele volta expandido.
 */
export function Janela(props: {
  readonly titulo: string;
  /** Uma linha abaixo do título, como na p.29: o que a janela faz. */
  readonly descricao?: string;
  readonly aberta: boolean;
  readonly aoFechar: () => void;
  /** Os botões do rodapé fixo, na ordem da p.29: voltar à esquerda, ação à direita. */
  readonly acoes?: ReactNode;
  /** 480, 560 ou 640 px (p.29). O padrão é 640. */
  readonly largura?: keyof typeof LARGURA_DO_MODAL;
  readonly children: ReactNode;
}) {
  return <Sobreposicao {...props} lugar="centro" />;
}

/**
 * A GAVETA da R01 (p.29) — a faixa do meio, que faltava.
 *
 * A p.29 tem três faixas, e o projeto só tinha duas: modal para a tarefa curta
 * (até 6 campos) e página inteira para o cadastro completo (mais de 10). A do
 * meio é a gaveta: "detalhe ou formulário médio sem sair da lista, à direita,
 * 400–560 px, altura total".
 *
 * A consequência de ela não existir foi medida: o cadastro de tanque, com sete
 * campos, foi para o modal de 640 px — um campo acima do limite do modal — e
 * ficou com 740 px de altura numa caixa de 85% da tela. Não era escolha de
 * quem escreveu a tela; era o único lugar que havia. Os tokens
 * `--gaveta-min`/`--gaveta-max` já estavam em `tokens.css`, declarados a
 * partir do manual, e nenhuma tela os usava.
 *
 * Ela não é um componente próprio: é o `lugar="direita"` da `Sobreposicao`
 * abaixo. É o MESMO `<dialog showModal()>` do modal, e de propósito — o
 * prendimento do foco, a devolução do foco a quem abriu, o `Esc` e o véu são
 * do navegador, e uma segunda implementação deles divergiria na primeira
 * correção. O que muda é onde a caixa assenta: `m-0 ml-auto` a encosta na
 * direita com altura inteira, em vez do `m-auto` que centra o modal.
 *
 * Abaixo de 768 px ela abre em tela cheia, como o modal: numa tela de bolso
 * uma gaveta de 400 px não deixa nada atrás para não sair de cima.
 */

/** O lugar define a caixa; o resto é igual nas duas (p.28, p.29).
 *
 *  - Modal: ao centro, raio 24, elevação 4; até 85% da tela; no celular abre de
 *    baixo, em tela cheia.
 *  - Gaveta: à direita, a 12 px da borda, raio 20, a altura toda; no celular,
 *    tela cheia.
 */
const CAIXA_DO_LUGAR = {
  centro:
    'm-auto min-w-[min(var(--modal-min),100vw)] max-w-[calc(100vw-var(--esp-12))] max-h-[var(--modal-altura-max)] rounded-modal bg-flutuante shadow-sobreposicao anima-modal max-[768px]:h-full max-[768px]:max-h-none max-[768px]:w-full max-[768px]:max-w-none max-[768px]:min-w-0 max-[768px]:rounded-none',
  direita:
    'm-0 ml-auto h-dvh max-h-none w-[var(--gaveta-max)] min-w-[min(var(--gaveta-min),100vw)] max-w-[calc(100vw-var(--esp-12))] bg-flutuante shadow-sobreposicao anima-gaveta min-[768px]:my-3 min-[768px]:mr-3 min-[768px]:h-[calc(100dvh-var(--esp-6))] min-[768px]:rounded-painel max-[768px]:w-full max-[768px]:max-w-none max-[768px]:min-w-0',
} as const;

/** As três larguras do modal (p.29). A gaveta tem a sua faixa, 400 a 560. */
const LARGURA_DO_MODAL = {
  p: 'w-[var(--modal-min)]',
  m: 'w-[var(--modal-m)]',
  g: 'w-[var(--modal-max)]',
} as const;

/** O corpo acompanha a caixa: o modal para em 85% da tela, a gaveta vai inteira. */
const CORPO_DO_LUGAR = {
  centro: 'flex max-h-[var(--modal-altura-max)] flex-col max-[768px]:h-full max-[768px]:max-h-none',
  direita: 'flex h-full flex-col',
} as const;

/**
 * A sobreposição com o lugar POR PARÂMETRO, para quem escolhe a faixa em
 * tempo de execução.
 *
 * `Janela` e `Gaveta` são o jeito normal de pedir uma das duas, e quem sabe
 * qual quer usa elas. Esta existe para a tela que decide a faixa pela regra da
 * R01 — o cadastro do combustível, que é modal com até 6 campos e gaveta a
 * partir de 7. A alternativa era `const Caixa = ... ? Gaveta : Janela`, e um
 * apelido local esconde o componente de quem lê o arquivo e do verificador do
 * rodapé fixo (H6), que confere pelo nome do que está escrito na tela.
 */
export function Sobreposicao({
  titulo,
  descricao,
  aberta,
  aoFechar,
  acoes,
  children,
  lugar,
  largura = 'g',
}: {
  readonly titulo: string;
  readonly descricao?: string;
  readonly aberta: boolean;
  readonly aoFechar: () => void;
  readonly acoes?: ReactNode;
  readonly children: ReactNode;
  readonly lugar: keyof typeof CAIXA_DO_LUGAR;
  readonly largura?: keyof typeof LARGURA_DO_MODAL;
}) {
  const caixa = useRef<HTMLDialogElement>(null);
  const painel = useContext(ContextoDoPainel);
  /* Quem devolve o painel de detalhe a forma de antes (ver `ContextoDoPainel`). */
  const devolver = useRef<(() => void) | null>(null);

  useEffect(() => {
    const d = caixa.current;
    if (!d) return;
    if (aberta && !d.open) {
      devolver.current = painel?.reduzir() ?? null;
      d.showModal();
      const alvo =
        d.querySelector<HTMLElement>(
          /* `[role="combobox"]` é o campo de escolha (`CampoSelecao`), que
             deixou de ser `<select>` em 20/10/2026 e continua sendo campo. */
          '[data-janela-corpo] :is(input:not([type="hidden"]):not([hidden]), select, textarea, button[role="combobox"]):not(:disabled)',
        ) ?? d.querySelector<HTMLElement>('[data-janela-corpo] button:not(:disabled), [data-janela-rodape] button:not(:disabled)');
      focarAoAbrir(d, alvo);
      /* Verificador da R01 (p.29), em desenvolvimento: as acoes moram no
         RODAPE FIXO (`acoes`), fora do corpo que rola. Com a barra dentro do
         corpo, "Gravar" rola junto com o formulario e, em tela cheia abaixo
         de 768 px, para no meio da tela. O `submit` do formulario vai para o
         rodape pelo atributo `form={id}`. */
      if (import.meta.env.DEV && d.querySelector('[data-janela-corpo] :is(.barra-de-acoes, button[type="submit"])')) {
        console.warn(
          `Sobreposição "${titulo}": botões de ação dentro do corpo que rola. A R01 (p.29) os põe no rodapé fixo — passe-os em \`acoes\`, com \`form={id}\` no botão que grava.`,
        );
      }
    }
    if (!aberta) {
      if (d.open) d.close();
      /* Fechou pelo botao, pelo `Esc` ou pelo `close()` de fora: o painel de
         onde ela nasceu volta a ser a janela larga que era. */
      devolver.current?.();
      devolver.current = null;
    }
  }, [aberta, painel, titulo]);

  return (
    <dialog
      ref={caixa}
      tabIndex={-1}
      aria-label={titulo}
      /* `close` cobre o Esc e o `close()` de fora; o estado de quem abriu
         precisa saber nos dois casos, senão a janela fecha e o estado diz
         "aberta" — e o próximo clique não reabre nada. */
      onClose={aoFechar}
      className={`overflow-hidden p-0 text-tinta backdrop:bg-sobreposicao ${CAIXA_DO_LUGAR[lugar]} ${lugar === 'centro' ? LARGURA_DO_MODAL[largura] : ''}`}
    >
      {aberta ? (
        <div className={CORPO_DO_LUGAR[lugar]}>
          <div className="flex shrink-0 items-start justify-between gap-4 px-6 pt-6 pb-4">
            <div className="flex min-w-0 flex-col gap-1">
              <span className="txt-secao text-tinta">{titulo}</span>
              {descricao ? <span className="txt-apoio text-tinta-secundaria">{descricao}</span> : null}
            </div>
            <BotaoFechar rotulo={lugar === 'direita' ? 'Fechar gaveta' : 'Fechar janela'} aoFechar={aoFechar} />
          </div>
          <div data-janela-corpo className="min-h-0 flex-auto overflow-auto px-6 pb-6">
            {children}
          </div>
          {acoes ? (
            <div
              data-janela-rodape
              className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-borda-sutil bg-rebaixada px-6 py-4 max-[768px]:flex-col max-[768px]:items-stretch"
            >
              {acoes}
            </div>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}

/** O botão que abre a janela, e a janela. Para a ação que só tem uma porta de entrada. */
export function JanelaDeAcao({
  rotulo,
  titulo,
  nivel = 'secundario',
  aberta,
  aoAbrir,
  aoFechar,
  desligado = false,
  acoes,
  children,
}: {
  /** O botão que abre. Verbo + objeto: "Registrar medição". */
  readonly rotulo: string;
  readonly titulo: string;
  readonly nivel?: NivelBotao;
  readonly aberta: boolean;
  readonly aoAbrir: () => void;
  readonly aoFechar: () => void;
  readonly desligado?: boolean;
  /** O rodapé fixo da janela (ver `Janela`). */
  readonly acoes?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <>
      <Botao nivel={nivel} disabled={desligado} onClick={aoAbrir}>
        {rotulo}
      </Botao>
      <Janela titulo={titulo} aberta={aberta} aoFechar={aoFechar} {...(acoes === undefined ? {} : { acoes })}>
        {children}
      </Janela>
    </>
  );
}
